import FormField from 'lib/src/components/form/FormField';
import { FormInputProps } from 'lib/src/types/shared/FormInputProps';
import {
    CSSProperties,
    SyntheticEvent,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import useFieldValidation from './hooks/useFieldValidation';

const TextArea: React.FC<TextAreaProps> = ({
    name,
    value,
    onChange,
    label = '',
    placeholder = '',
    required = false,
    validationRegExp,
    minLength,
    maxLength,
    disabled,
    customValidate,
    forceAsterisk = false,
    maxWordCount,
    style,
    extraClasses = '',
    minRows = 1,
    maxRows = 10,
}) => {
    const memoizedValidate = useCallback(_validate, [minLength, maxLength, validationRegExp]);
    const [error, showError] = useFieldValidation({
        name,
        required,
        value,
        customValidate,
        extendedValidate: memoizedValidate,
    });

    const [rows, setRows] = useState(minRows);
    const inputRef = useRef<HTMLTextAreaElement | null>(null);

    const wordCount = useMemo(() => {
        try {
            const split = value.split(' ');
            const filtered = split.filter(string => !!string);
            return filtered.length;
        } catch {
            return 0;
        }
    }, [value]);

    const fitRows = useCallback(() => {
        const input = inputRef.current;
        const prevRows = input?.rows;

        if (!value) {
            setRows(minRows);
            return;
        }

        if (input) {
            input.rows = minRows; // reset number of rows in textarea

            const computedInput = window.getComputedStyle(input);
            const padding =
                parseFloat(computedInput.paddingTop) + parseFloat(computedInput.paddingBottom);
            const lineHeight = parseFloat(computedInput.lineHeight);
            const scrollHeight = input.scrollHeight - padding;

            const curRows = Math.floor(scrollHeight / lineHeight);

            if (curRows === prevRows) {
                input.rows = curRows;
            }

            if (curRows >= maxRows) {
                input.rows = maxRows;
                input.scrollTop = input.scrollHeight;
            }
            setRows(curRows < maxRows ? curRows : maxRows);
        }
    }, [minRows, maxRows, value]);

    useEffect(() => {
        setTimeout(() => {
            fitRows();
        }, 0);
    }, [fitRows]);

    return (
        <FormField
            name={name}
            label={label}
            required={required}
            error={error}
            forceAsterisk={forceAsterisk}
        >
            <textarea
                ref={inputRef}
                rows={rows}
                className={`form-input text-area ${error ? 'error' : ''} ${extraClasses}`}
                placeholder={placeholder}
                name={name}
                id={name}
                value={value}
                onChange={handleChange}
                onBlur={handleBlur}
                disabled={disabled}
                style={style}
            />
            {maxWordCount && (
                <p className={`char-limit ${wordCount > maxWordCount ? 'red' : ''}`}>
                    {wordCount} word{wordCount !== 1 ? 's' : ''}
                </p>
            )}
        </FormField>
    );

    function handleChange(e: SyntheticEvent) {
        e.preventDefault();

        fitRows();
        onChange(name, (e.target as HTMLTextAreaElement).value);
    }

    function handleBlur() {
        showError();
    }

    function _validate(val: string) {
        if (!val) return;

        if (minLength && val.length < minLength) {
            return `Value must have at least ${minLength} characters.`;
        }
        if (maxLength && val.length > maxLength) {
            return `Value cannot have more than ${maxLength} characters.`;
        }
        if (validationRegExp && !RegExp(validationRegExp).test(val)) {
            return 'Invalid value provided.';
        }
    }
};

interface TextAreaProps extends FormInputProps<string> {
    placeholder?: string;
    label?: string;
    validationRegExp?: string;
    minLength?: number;
    maxLength?: number;
    forceAsterisk?: boolean;
    maxWordCount?: number;
    style?: CSSProperties;
    extraClasses?: string;
    minRows?: number;
    maxRows?: number;
}

export default TextArea;
